import { getCloudflareUrl } from "../../../lib/config"
import { getAuthToken, clearAuthToken } from "../../auth/storage"

export interface ApiError {
  error: string
  message?: string
  code?: string
  status: number
  /** Structured payload from web-api's shared `ErrorSchema`, if present. */
  details?: Record<string, unknown>
}

export class CloudflareApiError extends Error {
  public readonly status: number
  public readonly code?: string
  public readonly response: Response
  // The Response is already consumed here, so callers cannot recover this.
  public readonly details?: Record<string, unknown>

  // Grouped rather than positional: a fifth parameter trips max-params.
  constructor(
    message: string,
    status: number,
    extra: {
      code?: string
      response?: Response
      details?: Record<string, unknown>
    } = {}
  ) {
    super(message)
    this.name = "CloudflareApiError"
    this.status = status
    this.code = extra.code
    this.response = extra.response!
    this.details = extra.details
  }
}

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>
}

/**
 * Type-safe fetch wrapper for Cloudflare Workers API
 * Handles authentication, error handling, and request/response transformation
 */
class CloudflareApiClient {
  private readonly baseUrl: string

  constructor() {
    this.baseUrl = getCloudflareUrl()
  }

  get baseURL(): string {
    return this.baseUrl
  }

  private buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined | null>
  ): string {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`

    // Handle empty baseUrl (dev mode uses relative URLs through Vite proxy)
    if (!this.baseUrl) {
      if (params && Object.keys(params).length > 0) {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.set(key, String(value))
          }
        })
        return `${normalizedPath}?${searchParams.toString()}`
      }
      return normalizedPath
    }

    const url = new URL(normalizedPath, this.baseUrl)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.set(key, String(value))
        }
      })
    }

    return url.toString()
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    }

    const authToken = getAuthToken()
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const isJson = response.headers
      .get("content-type")
      ?.includes("application/json")

    if (!response.ok) {
      let errorData: ApiError | null = null

      if (isJson) {
        try {
          errorData = (await response.json()) as ApiError
        } catch {
          // Failed to parse error JSON
        }
      }

      // Handle authentication errors
      // Only clear token on 401 (unauthorized), not 403 (forbidden/lack of permissions)
      if (response.status === 401) {
        // eslint-disable-next-line no-console
        console.warn("Authentication error detected - clearing token")
        clearAuthToken()
        // eslint-disable-next-line no-console
        console.error("🚨 AUTH ERROR - THIS MIGHT CAUSE REDIRECT/REFRESH:", {
          status: response.status,
          url: response.url,
          timestamp: new Date().toISOString(),
        })
      }

      const message =
        errorData?.message ||
        errorData?.error ||
        `HTTP ${response.status}: ${response.statusText}`
      throw new CloudflareApiError(message, response.status, {
        // Routes are inconsistent: some send `code`, the auth routes send
        // `error` (INVALID_EMAIL, EMAIL_ALREADY_EXISTS). Accept either so
        // callers can branch on a code rather than match the message text.
        code: errorData?.code ?? errorData?.error,
        response,
        details: errorData?.details,
      })
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T
    }

    if (!isJson) {
      return response.text() as T
    }

    return (await response.json()) as T
  }

  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(path, params)

    const requestOptions: RequestInit = {
      method,
      headers: {
        ...this.getHeaders(),
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    }

    try {
      const response = await fetch(url, requestOptions)
      return await this.handleResponse<T>(response)
    } catch (error) {
      if (error instanceof CloudflareApiError) {
        throw error
      }

      // Network or other errors
      // eslint-disable-next-line no-console
      console.error("API request failed:", {
        method,
        url,
        error,
        stack: error instanceof Error ? error.stack : undefined,
      })
      throw new Error(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`
      )
    }
  }

  // HTTP method helpers
  async get<T>(
    path: string,
    options: Omit<RequestOptions, "body"> = {}
  ): Promise<T> {
    return await this.request<T>("GET", path, options)
  }

  async post<T>(
    path: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>("POST", path, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async put<T>(
    path: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>("PUT", path, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async delete<T = void>(
    path: string,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>("DELETE", path, options)
  }

  async patch<T>(
    path: string,
    body?: unknown,
    options: RequestOptions = {}
  ): Promise<T> {
    return await this.request<T>("PATCH", path, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  // POST and return raw Response (for binary downloads like PDFs)
  async postRaw(
    path: string,
    body: unknown,
    options: RequestOptions = {}
  ): Promise<Response> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(path, params)

    const response = await fetch(url, {
      method: "POST",
      headers: {
        ...this.getHeaders(),
        "Content-Type": "application/json",
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...fetchOptions.headers,
      },
      body: JSON.stringify(body),
      ...fetchOptions,
    })

    if (response.status === 401) {
      clearAuthToken()
    }

    return response
  }

  // Get raw response (for binary data like zip files)
  async getRaw(path: string, options: RequestOptions = {}): Promise<Response> {
    const { params, ...fetchOptions } = options
    const url = this.buildUrl(path, params)

    const requestOptions: RequestInit = {
      method: "GET",
      headers: {
        ...this.getHeaders(),
        Accept: "application/zip",
        // eslint-disable-next-line @typescript-eslint/no-misused-spread
        ...fetchOptions.headers,
      },
      ...fetchOptions,
    }

    const response = await fetch(url, requestOptions)

    // Still handle auth errors even for raw responses
    if (response.status === 401) {
      clearAuthToken()
    }

    return response
  }

  /**
   * POST with FormData (for file uploads)
   * Does NOT set Content-Type header - let the browser set multipart/form-data with boundary
   */
  async postFormData<T>(path: string, formData: FormData): Promise<T> {
    const url = this.buildUrl(path)
    const authToken = getAuthToken()

    const headers: Record<string, string> = {}
    if (authToken) {
      headers.authorization = `Bearer ${authToken}`
    }

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
      })
      return await this.handleResponse<T>(response)
    } catch (error) {
      if (error instanceof CloudflareApiError) {
        throw error
      }

      // eslint-disable-next-line no-console
      console.error("FormData upload failed:", {
        url,
        error,
        stack: error instanceof Error ? error.stack : undefined,
      })
      throw new Error(
        `Network error: ${error instanceof Error ? error.message : "Unknown error"}`
      )
    }
  }

  // File upload helper (for presigned URLs)
  async uploadFile(
    url: string,
    file: File,
    contentType?: string
  ): Promise<void> {
    const response = await fetch(url, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": contentType || file.type,
      },
    })

    if (!response.ok) {
      const errorText = await response
        .text()
        .catch(() => "Unable to read error response")
      // eslint-disable-next-line no-console
      console.error("❌ FILE UPLOAD FAILED:", {
        status: response.status,
        statusText: response.statusText,
        url,
        fileName: file.name,
        errorText,
      })

      throw new CloudflareApiError(
        `File upload failed: ${response.statusText} - ${errorText}`,
        response.status,
        { response }
      )
    }
  }
}

// Singleton instance
export const cfApi = new CloudflareApiClient()
